/**
 * Labelled text input with inline validation messaging.
 *
 * Errors are announced (`role="alert"`), wired with `aria-describedby` and
 * `aria-invalid`, and rendered next to the control — never as a toast.
 */
import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  /** Inline error; presence switches the field into its invalid state. */
  error?: string | null;
  hint?: ReactNode;
  /** Right-aligned live status, e.g. slug availability. */
  status?: ReactNode;
  prefix?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, hint, status, prefix, required, ...inputProps },
  ref
) {
  const generatedId = useId();
  const inputId = `field-${generatedId}`;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className={`field${error ? ' field--invalid' : ''}`}>
      <label className="field__label" htmlFor={inputId}>
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
        {required ? <span className="visually-hidden"> (required)</span> : null}
      </label>
      {prefix ? (
        <span className="wizard__slug-preview">
          {prefix}
          {String(inputProps.value ?? '')}
        </span>
      ) : null}
      <input
        {...inputProps}
        id={inputId}
        ref={ref}
        required={required}
        className="field__control"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy === '' ? undefined : describedBy}
      />
      {status ? <span className="field__status">{status}</span> : null}
      {hint ? (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
});
